import { StyledListings, StatusCell } from './style';
import { Button, Table, Dropdown, Input, DatePicker, Icon, Tag } from 'ui';
import { DrawerCancelListing } from 'ui/Drawer/Drawers/DrawerCancelListing';
import { DrawerViewListingApplication } from 'ui/Drawer/Drawers/DrawerViewListingApplication';
import DrawerSendMessage from 'ui/Drawer/Drawers/DrawerSendMessage';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'hooks/useAuth';
import { permissions } from 'config/config';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { message, Modal, MenuProps } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import useQueryApiClient from 'utils/useQueryApiClient';
import Filter from '../Filter';
import { Pagination } from 'types/Pagination';
import EnumSelect from '../../Selects/EnumSelect';
import type {
  Listing,
  ListingFilterState,
  ListingApplication,
} from '../../../types/ListingData';
import dayjs from 'dayjs';
import { ResponsiveWrapper } from 'admin/components/ResponsiveWrapper';
import MobileList from 'admin/components/MobileLayout/MobileList';
import { MobileListField } from 'ui/Card/MobileListCard';
import { adminPath } from 'enums/adminRoutes';
import { publicPath } from 'enums/publicRoutes';

const EDITABLE_STATUSES = [
  'unpublished',
  'published',
  'has_offers',
  'unsuccessful',
  'cancelled',
];

export interface ListingEditOptions {
  startOnFirstTab?: boolean;
}

interface ListingsProps {
  reload: number;
  onEdit: (listingId: string, options?: ListingEditOptions) => void;
  filter?: ListingFilterState;
  onFilterChange?: (filter: ListingFilterState) => void;
  onPaginationChange?: (status: { total?: number }) => void;
  filterContent?: React.ReactNode;
}

const Listings = ({
  reload = 0,
  onEdit,
  filter,
  onFilterChange,
  onPaginationChange,
  filterContent,
}: ListingsProps) => {
  const { can } = useAuth();
  const canEdit = can(permissions.edit_farm_listing);
  const [internalFilter, setInternalFilter] = useState<ListingFilterState>({});
  const advancedSearch = filter ?? internalFilter;
  const setAdvancedSearch = onFilterChange ?? setInternalFilter;
  const [paginationStatus, setPaginationStatus] = useState<Pagination>({
    total: 0,
    page: 1,
    page_size: 15,
  });
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [isCancelDrawerOpen, setIsCancelDrawerOpen] = useState(false);
  const [selectedListingId, setSelectedListingId] = useState<string | null>(
    null
  );
  const [isApplicationDrawerOpen, setIsApplicationDrawerOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] =
    useState<ListingApplication | null>(null);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [applicationToFetch, setApplicationToFetch] = useState<string | null>(
    null
  );
  const [messageDrawerOpen, setMessageDrawerOpen] = useState(false);
  const [messageListingId, setMessageListingId] = useState<string | null>(null);
  const intl = useIntl();
  const navigate = useNavigate();

  const isEditableStatus = (status: string | undefined) => {
    return status && EDITABLE_STATUSES.includes(status);
  };

  useEffect(() => {
    if (reload > 0) {
      setReloadTrigger((prev) => prev + 1);
    }
  }, [reload]);

  const { appendData: deleteListing } = useQueryApiClient({
    request: {
      url: `/api/v2/listings/:id`,
      method: 'DELETE',
    },
    onSuccess: () => {
      message.success(
        intl.formatMessage({ id: 'message.successfully_deleted_listing' })
      );
      setReloadTrigger((prev) => prev + 1);
    },
    onError: (response) => {
      message.error(intl.formatMessage({ id: `message.${response.error}` }));
    },
  });

  const { appendData: copyListing } = useQueryApiClient({
    request: {
      url: `/api/v2/listings/:id/duplicate`,
      method: 'POST',
    },
    onSuccess: (response) => {
      setReloadTrigger((prev) => prev + 1);
      onEdit(response.id);
    },
    onError: (response) => {
      message.error(intl.formatMessage({ id: `message.${response.error}` }));
    },
  });

  const handleDelete = (listingId: string): void => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'message.confirm_delete_listing' }),
      okText: intl.formatMessage({ id: 'general.yes' }),
      cancelText: intl.formatMessage({ id: 'general.no' }),
      onOk: () => {
        deleteListing(null, { id: listingId });
      },
    });
  };

  const handleEdit = (listingId: string): void => {
    onEdit(listingId, { startOnFirstTab: true });
  };

  const handleCancel = (listingId: string): void => {
    setSelectedListingId(listingId);
    setIsCancelDrawerOpen(true);
  };

  useQueryApiClient({
    request: {
      url: `/api/v2/listings/${applicationToFetch}/applications`,
    },
    enabled: !!applicationToFetch,
    onSuccess: (applications: ListingApplication[]) => {
      const acceptedApplication = applications.find(
        (app) => app.status === 'accepted'
      );

      if (acceptedApplication) {
        setSelectedApplication(acceptedApplication);
        setIsApplicationDrawerOpen(true);
      } else {
        setSelectedListing(null);
      }

      setApplicationToFetch(null);
    },
    onError: (error) => {
      message.error(intl.formatMessage({ id: 'message.error' }));
      setApplicationToFetch(null);
      setSelectedListing(null);
    },
  });

  const onRow = useCallback(
    (record: Listing) => ({
      onClick: () => {
        onEdit(record.id);
      },
      style: {
        cursor: 'pointer',
      },
    }),
    [onEdit]
  );

  const columns = [
    {
      title: intl.formatMessage({ id: 'listings.title' }),
      dataIndex: 'title',
      render: (value: string) => value,
    },
    {
      title: intl.formatMessage({ id: 'listings.transaction_type' }),
      dataIndex: 'transaction_type',
      render: (value: string | null) =>
        value ? intl.formatMessage({ id: `enum.${value.toUpperCase()}` }) : '',
    },
    {
      title: intl.formatMessage({ id: 'listings.status' }),
      dataIndex: 'status',
      render: (value: string) => (
        <StatusCell>
          <Tag className={`status ${value}`}>
            {intl.formatMessage({ id: `listings.${value}` })}
          </Tag>
        </StatusCell>
      ),
    },
    {
      title: intl.formatMessage({ id: 'listings.publication_start_date' }),
      dataIndex: 'publication_start_date',
      render: (value: string) =>
        value && dayjs(value).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: intl.formatMessage({ id: 'listings.publication_end_date' }),
      dataIndex: 'publication_end_date',
      render: (value: string) =>
        value && dayjs(value).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: intl.formatMessage({ id: 'listings.results' }),
      dataIndex: 'application_count',
      render: (value: number, record: Listing) => {
        const hasApplications = !!value && value > 0;

        if (!hasApplications) {
          return '';
        }

        const label = `${value} ${intl.formatMessage({ id: 'listings.applications' })}`;

        if (record.status === 'has_offers') {
          return <span className="results-label">{label}</span>;
        }

        return (
          <Button
            type="link"
            className="link"
            label={
              <span className="link-with-icon">
                {label}
                <Icon className="link-icon" name="admin/link" type="small" />
              </span>
            }
            onClick={() => navigate(adminPath.listingApplications(record.id))}
          />
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'listings.identifier' }),
      dataIndex: 'identifier',
      render: (value: string) => value,
    },
    {
      title: (
        <div className={'actions-title'}>
          {intl.formatMessage({ id: 'listings.actions' })}
        </div>
      ),
      render: (record: Listing) => {
        const getMenuItems = (): MenuProps['items'] => {
          const items: MenuProps['items'] = [];

          if (canEdit && isEditableStatus(record.status)) {
            items.push({
              label: intl.formatMessage({ id: 'listings.edit' }),
              key: 'edit',
            });
          }

          if (
            canEdit &&
            [
              'published',
              'has_offers',
              'unsuccessful',
              'closed',
              'completed',
            ].includes(record.status)
          ) {
            items.push({
              label: intl.formatMessage({ id: 'listings.cancel' }),
              key: 'cancel',
            });
          }

          if (['partially_completed', 'completed'].includes(record.status)) {
            items.push({
              label: intl.formatMessage({ id: 'listings.copy_summary_link' }),
              key: 'copy_summary_link',
            });
          }

          if (canEdit) {
            items.push({
              label: intl.formatMessage({ id: 'listings.copy' }),
              key: 'copy',
            });
          }

          if (record.application_count && record.application_count > 0) {
            items.push({
              label: intl.formatMessage({ id: 'listings.message' }),
              key: 'message',
            });
          }

          if (
            ['closed', 'completed', 'partially_completed'].includes(
              record.status
            )
          ) {
            items.push({
              label: intl.formatMessage({ id: 'general.transactions' }),
              key: 'transactions',
            });
          }

          if (record.status !== 'unpublished') {
            items.push({
              label: intl.formatMessage({
                id: 'listings.applications.view_listing',
              }),
              key: 'view_listing',
            });
          }

          if (canEdit) {
            items.push({
              label: intl.formatMessage({ id: 'listings.delete' }),
              key: 'delete',
              danger: true,
            });
          }

          return items;
        };

        const menuItems = getMenuItems();

        return menuItems && menuItems.length > 0 ? (
          <Dropdown
            menu={{
              items: menuItems,
              onClick: (value) => {
                value.domEvent?.stopPropagation?.();
                switch (value.key) {
                  case 'edit':
                    handleEdit(record.id);
                    break;
                  case 'cancel':
                    handleCancel(record.id);
                    break;
                  case 'copy_summary_link':
                    navigator.clipboard.writeText(
                      `${window.location.origin}${publicPath.wholesaleListingSummary(record.id)}`
                    );
                    message.success(
                      intl.formatMessage({ id: 'listing.summary.link_copied' })
                    );
                    break;
                  case 'copy':
                    copyListing([], { id: record.id });
                    break;
                  case 'message':
                    setMessageListingId(record.id);
                    setMessageDrawerOpen(true);
                    break;
                  case 'transactions':
                    setSelectedListing(record);
                    setApplicationToFetch(record.id);
                    break;
                  case 'view_listing':
                    navigate(publicPath.wholesaleListingDetail(record.id));
                    break;
                  case 'delete':
                    handleDelete(record.id);
                    break;
                }
              },
            }}
          >
            <Button
              className="action"
              label={intl.formatMessage({ id: 'listings.actions' })}
              icon={<DownOutlined />}
              iconPosition="end"
              size="middle"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            />
          </Dropdown>
        ) : null;
      },
    },
  ];

  const mobileItems: {
    coverFields: MobileListField[];
    bodyFields: MobileListField[];
    footerFields: MobileListField[];
  } = useMemo(
    () => ({
      coverFields: [
        {
          name: 'title',
          render: (value: string) => (
            <div className="mobile-list-card-cover-title">
              <span className="label">
                {intl.formatMessage({ id: 'listings.title' })}
              </span>
              <span className="value">{value || '-'}</span>
            </div>
          ),
        },
      ],
      bodyFields: [
        {
          name: 'transaction_type',
          label: intl.formatMessage({ id: 'listings.transaction_type' }),
          render: (value: string | null) =>
            value
              ? intl.formatMessage({ id: `enum.${value.toUpperCase()}` })
              : '-',
        },
        {
          name: 'status',
          label: intl.formatMessage({ id: 'listings.status' }),
          render: (value: string) => (
            <StatusCell>
              <Tag className={`status ${value}`}>
                {intl.formatMessage({ id: `listings.${value}` })}
              </Tag>
            </StatusCell>
          ),
        },
        {
          name: 'publication_start_date',
          label: intl.formatMessage({
            id: 'listings.publication_start_date',
          }),
          render: (value: string) =>
            value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '-',
        },
        {
          name: 'publication_end_date',
          label: intl.formatMessage({
            id: 'listings.publication_end_date',
          }),
          render: (value: string) =>
            value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '-',
        },
        {
          name: 'application_count',
          label: intl.formatMessage({ id: 'listings.results' }),
          render: (value: number, record: Listing) => {
            if (!value || value <= 0) return '-';
            const label = `${value} ${intl.formatMessage({ id: 'listings.applications' })}`;
            if (record.status === 'has_offers') return label;
            return (
              <Button
                type="link"
                className="link"
                label={label}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  navigate(adminPath.listingApplications(record.id));
                }}
              />
            );
          },
        },
        {
          name: 'identifier',
          label: intl.formatMessage({ id: 'listings.identifier' }),
          render: (value: string) => value || '-',
        },
      ],
      footerFields: [
        {
          name: 'actions',
          render: (_: unknown, record: Listing) => {
            const getMenuItems = (): MenuProps['items'] => {
              const items: MenuProps['items'] = [];

              if (canEdit && isEditableStatus(record.status)) {
                items.push({
                  label: intl.formatMessage({ id: 'listings.edit' }),
                  key: 'edit',
                });
              }

              if (
                canEdit &&
                [
                  'published',
                  'has_offers',
                  'unsuccessful',
                  'closed',
                  'completed',
                ].includes(record.status)
              ) {
                items.push({
                  label: intl.formatMessage({ id: 'listings.cancel' }),
                  key: 'cancel',
                });
              }

              if (
                ['partially_completed', 'completed'].includes(record.status)
              ) {
                items.push({
                  label: intl.formatMessage({
                    id: 'listings.copy_summary_link',
                  }),
                  key: 'copy_summary_link',
                });
              }

              if (canEdit) {
                items.push({
                  label: intl.formatMessage({ id: 'listings.copy' }),
                  key: 'copy',
                });
              }

              if (record.application_count && record.application_count > 0) {
                items.push({
                  label: intl.formatMessage({ id: 'listings.message' }),
                  key: 'message',
                });
              }

              if (
                ['closed', 'completed', 'partially_completed'].includes(
                  record.status
                )
              ) {
                items.push({
                  label: intl.formatMessage({ id: 'general.transactions' }),
                  key: 'transactions',
                });
              }

              if (record.status !== 'unpublished') {
                items.push({
                  label: intl.formatMessage({
                    id: 'listings.applications.view_listing',
                  }),
                  key: 'view_listing',
                });
              }

              if (canEdit) {
                items.push({
                  label: intl.formatMessage({ id: 'listings.delete' }),
                  key: 'delete',
                  danger: true,
                });
              }

              return items;
            };

            const menuItems = getMenuItems();

            return menuItems && menuItems.length > 0 ? (
              <div onClick={(e) => e.stopPropagation()}>
                <Dropdown
                  menu={{
                    items: menuItems,
                    onClick: (value) => {
                      value.domEvent?.stopPropagation?.();
                      switch (value.key) {
                        case 'edit':
                          handleEdit(record.id);
                          break;
                        case 'cancel':
                          handleCancel(record.id);
                          break;
                        case 'copy_summary_link':
                          navigator.clipboard.writeText(
                            `${window.location.origin}${publicPath.wholesaleListingSummary(record.id)}`
                          );
                          message.success(
                            intl.formatMessage({
                              id: 'listing.summary.link_copied',
                            })
                          );
                          break;
                        case 'copy':
                          copyListing([], { id: record.id });
                          break;
                        case 'message':
                          setMessageListingId(record.id);
                          setMessageDrawerOpen(true);
                          break;
                        case 'transactions':
                          setSelectedListing(record);
                          setApplicationToFetch(record.id);
                          break;
                        case 'view_listing':
                          navigate(
                            publicPath.wholesaleListingDetail(record.id)
                          );
                          break;
                        case 'delete':
                          handleDelete(record.id);
                          break;
                      }
                    },
                  }}
                >
                  <Button
                    className="action"
                    label={intl.formatMessage({ id: 'listings.actions' })}
                    icon={<DownOutlined />}
                    iconPosition="end"
                    size="small"
                  />
                </Dropdown>
              </div>
            ) : null;
          },
        },
      ],
    }),
    [intl, canEdit, isEditableStatus]
  );

  return (
    <StyledListings>
      <ResponsiveWrapper
        desktopComponent={
          <>
            {filterContent ?? (
              <Filter
                onFinish={setAdvancedSearch}
                totalRecord={paginationStatus?.total ?? 0}
                isLabelVisible={true}
                defaultFilterParam={advancedSearch}
              >
                <Input
                  label={intl.formatMessage({ id: 'listings.search' })}
                  name="search"
                  placeholder={intl.formatMessage({
                    id: 'listings.search_placeholder',
                  })}
                  allowClear
                />
                <EnumSelect
                  label={intl.formatMessage({
                    id: 'listings.filter_by_status',
                  })}
                  name="status"
                  mode="multiple"
                  code="ListingStatus"
                />
                <EnumSelect
                  label={intl.formatMessage({ id: 'listings.filter_by_type' })}
                  name="transaction_type"
                  mode="multiple"
                  code="ListingTransactionType"
                />
                <DatePicker
                  label={intl.formatMessage({
                    id: 'listings.filter_by_publication_end',
                  })}
                  name="publication_date_to"
                />
                <Button
                  type="primary"
                  label={intl.formatMessage({ id: 'listings.apply_filter' })}
                  htmlType="submit"
                />
              </Filter>
            )}
            <Table
              url={`/api/v2/listings`}
              columns={columns}
              filter={advancedSearch}
              rowKey="id"
              reload={reloadTrigger}
              onRow={onRow}
              paginnationStatus={(status) => {
                setPaginationStatus(status);
                onPaginationChange?.(status as { total?: number });
              }}
            />
          </>
        }
        mobileComponent={
          <MobileList
            url="/api/v2/listings"
            filter={advancedSearch}
            reload={reloadTrigger}
            rowKey="id"
            coverFields={mobileItems.coverFields}
            bodyFields={mobileItems.bodyFields}
            footerFields={mobileItems.footerFields}
            paginnationStatus={(status) => {
              setPaginationStatus(status);
              onPaginationChange?.(status as { total?: number });
            }}
            page_size={15}
            onItemClick={(record: Listing) => onEdit(record.id)}
          />
        }
      />
      <DrawerCancelListing
        open={isCancelDrawerOpen}
        onClose={() => {
          setIsCancelDrawerOpen(false);
          setSelectedListingId(null);
        }}
        listingId={selectedListingId || ''}
        setReload={setReloadTrigger}
      />
      <DrawerViewListingApplication
        open={isApplicationDrawerOpen}
        onClose={() => {
          setIsApplicationDrawerOpen(false);
          setSelectedApplication(null);
          setSelectedListing(null);
        }}
        application={selectedApplication}
        listing={selectedListing}
        applications={selectedListing?.applications || []}
        onActionCompleted={() => {
          setReloadTrigger((prev) => prev + 1);
        }}
      />
      <DrawerSendMessage
        open={messageDrawerOpen}
        onClose={() => {
          setMessageDrawerOpen(false);
          setMessageListingId(null);
        }}
        context={
          messageListingId
            ? { type: 'listing', listingId: messageListingId }
            : undefined
        }
      />
    </StyledListings>
  );
};

export default Listings;
